/**
 * Task dispatch hook — v1 command path.
 *
 * `TaskDispatchForm.tsx` used to call the legacy `useDispatchTask()`
 * (in `hooks/index.ts`), which POSTs to `${apiBaseUrl}/tasks/dispatch_task`
 * — a bare, non-`/api/v1/`-prefixed path that only ever existed on
 * the mock server's legacy routes. The real `nexsoss-api-server`
 * mounts every route under `/api/v1/sites/{site}/...` exclusively,
 * so that request 405'd against the real backend. `dispatchTask()`
 * in `v1-client.ts` (`POST /api/v1/sites/{site}/commands/dispatch_task`)
 * was already correctly implemented and simply unused.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSafeSiteId } from '../../state/useActiveSite';
import { tasksQueryOptions } from '../query-options';
import { dispatchTask } from '../v1-client';
import type { DispatchTaskResponse, SiteId, TaskRequest } from '../v1';

export interface DispatchTaskVars {
  task: TaskRequest;
  /** Which UI flow originated this dispatch (plain form vs. the
   *  calendar-slot modal) — see `DispatchTaskRequest.requested_via`. */
  requestedVia?: 'dashboard' | 'calendar';
}

export function useDispatchTaskV1() {
  const qc = useQueryClient();
  const siteId = useSafeSiteId();
  return useMutation<DispatchTaskResponse, Error, DispatchTaskVars>({
    mutationFn: ({ task, requestedVia }) =>
      dispatchTask(siteId as SiteId, {
        task,
        idempotency_key: crypto.randomUUID(),
        requested_via: requestedVia,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: tasksQueryOptions(siteId ? (siteId as SiteId) : null).queryKey }),
  });
}
